import React, { useState, useMemo, useRef } from "react";
import styles from "./table.module.css";
import { MyIcons } from "../../icons/my-icons";

const Table = ({
  headers,
  data,
  onPress,
  renderActions,
  textColorConditions = {},
  renderIcon,
  expandAllByDefault = false,
  getRowClass,
  getInvoiceRowClass,
  getInvoiceRowTitle,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Expandir todas las filas por defecto si expandAllByDefault es true
  React.useEffect(() => {
    if (expandAllByDefault && data.length > 0) {
      const allExpanded = {};
      data.forEach((row, index) => {
        if (row.invoices && row.invoices.length > 0) {
          allExpanded[index] = true;
        }
      });
      setExpandedRows(allExpanded);
    }
  }, [data, expandAllByDefault]);

  const handleRowClick = (row, rowIndex, event) => {
    // Detect middle click or modifier (Ctrl/Cmd)
    const isMiddle = !!(event && (event.nativeEvent?.button === 1 || event.button === 1 || event.which === 2));
    const isModifier = !!(event && (event.ctrlKey || event.metaKey));

    if ((isMiddle || isModifier) && onPress) {
      // If user requested opening in new tab, call onPress and don't toggle expansion
      onPress(row, event);
      return;
    }

    if (row.invoices && row.invoices.length > 0) {
      setExpandedRows((prev) => ({
        ...prev,
        [rowIndex]: !prev[rowIndex],
      }));
    } else if (onPress) {
      onPress(row, event);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      // Obtener valores, manejando claves anidadas
      const aValue = sortConfig.key.split('.').reduce((acc, part) => acc && acc[part], a);
      const bValue = sortConfig.key.split('.').reduce((acc, part) => acc && acc[part], b);

      // Manejar valores nulos o indefinidos
      if (aValue === null || aValue === undefined || aValue === "" || aValue === "-- --") return 1;
      if (bValue === null || bValue === undefined || bValue === "" || bValue === "-- --") return -1;

      // Convertir a string para análisis
      const aStr = String(aValue);
      const bStr = String(bValue);

      // Detectar si son números PUROS (sin texto mezclado)
      const aIsPureNumber = /^-?\d+\.?\d*$/.test(aStr.trim());
      const bIsPureNumber = /^-?\d+\.?\d*$/.test(bStr.trim());
      
      if (aIsPureNumber && bIsPureNumber) {
        // Ordenamiento numérico puro
        const aNum = parseFloat(aStr);
        const bNum = parseFloat(bStr);
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Ordenamiento alfanumérico natural usando localeCompare con opción numeric
      const result = aStr.toLowerCase().localeCompare(bStr.toLowerCase(), 'es', {
        numeric: true,
        sensitivity: 'base'
      });
      
      return sortConfig.direction === "asc" ? result : -result;
    });

    return sorted;
  }, [data, sortConfig]);

const renderCellValue = (row, key, isInvoice = false) => {
  // Manejo de claves anidadas (e.g., "compania.nombre")
  const value = key.split('.').reduce((acc, part) => acc && acc[part], row);

  if (value === undefined || value === null || value === "") {
    return "-- --";
  }
  return value;
};

  const getCellStyle = (row, key) => {
    const style = {};

    if (textColorConditions[key]) {
      const value = row[key];
      const conditions = textColorConditions[key];

      for (const condition of conditions) {
        if (condition.test(value, row)) {
          style.color = condition.color;
          break;
        }
      }
    }

    return style;
  };

  const wrapperRef = useRef(null);

  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const onMouseDown = (e) => {
      // Prevent browser autoscroll when middle-clicking on table rows
      try {
        if (e.button === 1) {
          const target = e.target && e.target.closest && e.target.closest('tr');
          if (target && target.classList.contains(styles.row)) {
            e.preventDefault();
          }
        }
      } catch (err) {
        // ignore
      }
    };

    const onAuxClick = (e) => {
      // Nothing to do here; React's onAuxClick/onClick on the rows will handle opening.
      // We keep this listener in case we want to add global behavior later.
    };

    el.addEventListener('mousedown', onMouseDown, { capture: true, passive: false });
    el.addEventListener('auxclick', onAuxClick, { capture: true });

    return () => {
      el.removeEventListener('mousedown', onMouseDown, { capture: true, passive: false });
      el.removeEventListener('auxclick', onAuxClick, { capture: true });
    };
  }, []);

  return (
    <div className={styles.tableWrapper} ref={wrapperRef}>
      <table className={styles.table}>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th 
                key={index} 
                className={styles.headerCell}
                onClick={() => handleSort(header.key)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                  <span>{header.label}</span>
                  {sortConfig.key === header.key && sortConfig.direction && (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {sortConfig.direction === "asc" ? (
                        <MyIcons name="arrowUp" width={14} height={14} color="var(--black)" />
                      ) : (
                        <MyIcons name="arrowDown" width={14} height={14} color="var(--black)" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
            {renderActions && <th className={styles.headerCell}>Acciones</th>}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              <tr
                className={`${styles.row} ${
                  row.invoices?.length > 0 ? styles.expandableRow : ""
                } ${getRowClass?.(row, styles) || ""}`}
                data-expediente={row.expedienteId || row.expediente || null}
                style={{
                  cursor:
                    row.invoices?.length > 0 || onPress ? "pointer" : "default",
                }}
                onClick={(e) => handleRowClick(row, rowIndex, e)}
                onAuxClick={(e) => handleRowClick(row, rowIndex, e)}
              >
                {headers.map((header, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={styles.cell}
                    style={getCellStyle(row, header.key)}
                  >
                    <div className={styles.cellContent}>
                      {cellIndex === 0 && row.invoices?.length > 0 && (
                        <span className={styles.expandIcon}>
                          {expandedRows[rowIndex] ? (
                            <MyIcons
                              name={"arrowDown"}
                              color={"var(--black)"}
                              height={20}
                              width={20}
                            />
                          ) : (
                            <MyIcons
                              color={"var(--black)"}
                              name={"arrowRight"}
                              height={20}
                              width={20}
                            />
                          )}
                        </span>
                      )}
                      <span className={styles.cellText}>
                        {renderCellValue(row, header.key)}
                      </span>
                    </div>
                  </td>
                ))}
                {renderActions && (
                  <td
                    className={`${styles.cell} ${styles.actionsCell}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderActions(row)}
                  </td>
                )}
              </tr>
                {expandedRows[rowIndex] &&
                row.invoices &&
                row.invoices.map((invoice, invoiceIndex) => (
                  <tr
                    key={`invoice-${rowIndex}-${invoiceIndex}`}
                    className={`${styles.invoiceRow} ${getInvoiceRowClass?.(invoice, styles) || ""}`}
                    title={getInvoiceRowTitle?.(invoice) || undefined}
                    data-expediente={invoice.expedienteId || invoice.expediente || null}
                    onClick={(e) => onPress && onPress(invoice, e)}
                    onAuxClick={(e) => onPress && onPress(invoice, e)}
                  >
                    {headers.map((header, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={styles.cell}
                        style={getCellStyle(invoice, header.key)}
                      >
                        <div className={styles.cellContent}>
                          {cellIndex === 0 && (
                            <span className={styles.indentSpace}></span>
                          )}
                          <span className={styles.cellText}>
                            {renderCellValue(invoice, header.key, true)}
                          </span>
                        </div>
                      </td>
                    ))}
                    {renderActions && (
                      <td
                        className={`${styles.cell} ${styles.actionsCell}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderActions({ ...invoice, isInvoice: true })}
                      </td>
                    )}
                  </tr>
                ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;